import { ServiceAssignment } from '../types';
import { parseDecimalInput } from './money';

/**
 * Aufbereitung der KI-Antwort des LV-Imports (Ausschreibung -> Angebot).
 *
 * Warum eigenes Modul: das Modal hat die Rohzeilen frueher 1:1 uebernommen und
 * alles vorausgewaehlt. Ein Leistungsverzeichnis besteht aber nicht nur aus
 * Leistungszeilen — Titel, Zwischensummen und Uebertraege stehen dazwischen.
 * Uebernommen wurden sie trotzdem, und weil der Positionszaehler
 * (quoteLogic.computePosNumbers) stumpf durchzaehlt, verschob JEDE solche
 * Phantomzeile alle folgenden Positionsnummern — im Editor und im PDF. Genau
 * das ist das gemeldete "landet an falschen Stellen".
 *
 * Zweite Ursache: Zeilen ohne erkannten Abschnitt landeten in der Catch-all-
 * Gruppe, die im Angebot IMMER zuletzt gerendert wird. Eine Position aus der
 * Mitte des Dokuments rutschte damit ans Ende. Hier erben solche Zeilen den
 * zuletzt gesehenen Abschnitt — die KI liefert die Zeilen in Dokumentreihen-
 * folge, also ist der letzte gesehene Titel der richtige.
 *
 * DRITTE Ursache (2026-08-20): die Einstufung selbst war der groesste Schaden.
 * "Weder Menge noch Einheit" hiess bisher pauschal "Ueberschrift" — und eine
 * Ueberschrift setzt den Abschnitt ALLER folgenden Zeilen neu. Damit kippte
 * jede Pauschal-, Bedarfs- und Nur-Text-Position und jede nicht erkannte
 * Summenzeile ("Gesamt Titel 1", "MwSt. 19 %", "Titelsumme") die komplette
 * Gruppierung ab ihrer Stelle. Die Einstufung ist deshalb jetzt asymmetrisch:
 * im Zweifel Leistung, denn eine falsch uebernommene Ueberschrift sieht der
 * Nutzer als 0-Euro-Zeile und loescht sie, waehrend eine als Ueberschrift
 * verschluckte Position spurlos verschwindet UND alles danach vergiftet.
 *
 * Rein und ohne React/Supabase, damit die gesamte Ableitung ohne DOM-Test-
 * umgebung pruefbar bleibt.
 */

/** Eine vom Auftraggeber vorgegebene LV-Zeile (Preise bewusst NICHT enthalten). */
export interface TenderLvRow {
    position: string;
    bezeichnung: string;
    einheit: string;
    menge: number;
    abschnitt: string;
    /**
     * KI-geschaetzte Leistungsart -> steuert die Modul-Zuordnung im Projekt,
     * wenn aus dem Angebot ein Auftrag/Projekt angelegt wird (quoteToReport).
     */
    assignment: ServiceAssignment;
    /**
     * Bedarfs-/Eventualposition (NEP). Der Proxy-Prompt des LV-Imports kennt
     * das Feld nicht, deshalb wird es hier aus dem Leistungstext gelesen —
     * sonst zaehlen Bedarfspositionen voll in die Angebotssumme.
     */
    nep: boolean;
}

/**
 * Was die Zeile im Dokument wirklich ist.
 *  - service:  echte Leistungszeile -> wird uebernommen
 *  - heading:  Titel-/Losueberschrift -> wird zum Abschnitt der folgenden Zeilen
 *  - sum:      Zwischensumme/Uebertrag/Steuerzeile -> gehoert nicht ins Angebot
 *  - empty:    ohne Bezeichnung -> unbrauchbar
 */
export type TenderLvRowKind = 'service' | 'heading' | 'sum' | 'empty';

export interface TenderLvRowDraft extends TenderLvRow {
    kind: TenderLvRowKind;
    /** Klartext fuer das Review, warum die Zeile nicht als Leistung gilt. '' bei 'service'. */
    note: string;
    /** Vorauswahl. Nur echte, noch nicht vorhandene Leistungszeilen sind angehakt. */
    selected: boolean;
    /** Steht mit gleicher LV-Nummer/Bezeichnung bereits im Angebot (Re-Import). */
    duplicate: boolean;
    /**
     * Zeile ist zwar als Leistung eingestuft, aber unvollstaendig (keine Menge
     * und keine Einheit) — im Review hervorheben statt still uebernehmen.
     */
    needsReview: boolean;
    /**
     * Der Abschnitt, wie die KI ihn geliefert hat — VOR der Vererbung. Ohne
     * ihn liesse sich die Vererbung nach einer Korrektur im Review nicht neu
     * berechnen: `abschnitt` traegt dann schon den geerbten Wert und ein
     * zweiter Durchlauf wuerde den Fehler zementieren.
     */
    sourceAbschnitt: string;
    /**
     * Vom Nutzer von Hand gesetzter Abschnitt. Getrennt von `sourceAbschnitt`,
     * weil auch der LEERE Wert eine Entscheidung ist: ohne die Trennung fuellte
     * die Vererbung ein geleertes Feld sofort wieder auf, und eine Position
     * liess sich nicht mehr aus ihrem Abschnitt herausnehmen.
     */
    abschnittOverride?: string;
    /**
     * Der Nutzer hat den Haken selbst gesetzt oder entfernt.
     *
     * Ohne diese Marke war jede Handentscheidung fluechtig: die Neuableitung
     * nach einer Korrektur an IRGENDEINER Zeile rechnete die Vorauswahl neu,
     * und ein von Hand angehakter Titel fiel still zurueck. Umgekehrt kam ein
     * Haken NICHT wieder, wenn das Feld beim Tippen kurz leer war und die
     * Zeile dadurch zwischenzeitlich als Ueberschrift galt — die Position
     * verschwand lautlos aus dem Angebot. Beides war dieselbe Luecke.
     */
    selectionTouched?: boolean;
}

const ASSIGNMENTS: ServiceAssignment[] = [
    'none', 'inspection', 'einweisung', 'sigeplan', 'asplan', 'unterlage', 'vorank', 'site_rules', 'planning_general', 'additional',
];
const ASSIGNMENT_SET = new Set<string>(ASSIGNMENTS);

export const normalizeAssignment = (v: unknown): ServiceAssignment =>
    (typeof v === 'string' && ASSIGNMENT_SET.has(v)) ? (v as ServiceAssignment) : 'none';

/**
 * Zeilen, die im LV zwischen den Leistungen stehen, aber keine sind. Bewusst
 * als Wortanfang/ganzes Wort geprueft: "Summe" darf nicht in
 * "Summenschienen-Abdeckung" treffen.
 *
 * Die urspruengliche Liste war zu kurz — "Gesamt Titel 1", "Titelsumme",
 * "MwSt. 19 %", "zzgl. Umsatzsteuer" und "Netto" fielen durch, standen ohne
 * Menge/Einheit da, galten damit als Ueberschrift und vergifteten den
 * Abschnitt aller Folgezeilen. Das ist das gemeldete "Zwischensummen
 * ausgelassen". Die Erweiterung traf dann aber echte Leistungstexte, deshalb
 * die Zweiteilung: stark und schwach.
 */

/**
 * EINDEUTIGE Summenwoerter. Eine Zeile, die so beginnt, ist auch dann keine
 * Leistung, wenn sie eine Ordnungszahl traegt — GAEB-Formulare schreiben die
 * Titelnummer regelmaessig in die Pos-Spalte der Titelsummenzeile.
 * Nur Menge UND Einheit koennen sie noch schlagen.
 */
const SUM_PATTERNS_STRONG = [
    'zwischensumme',
    'gesamtsumme',
    'endsumme',
    'nettosumme',
    'bruttosumme',
    'titelsumme',
    'angebotssumme',
    'hauptsumme',
    'schlusssumme',
    'zwischenergebnis',
    'uebertrag',
    'übertrag',
    'summe',
    'summen',
    'umsatzsteuer',
    'mehrwertsteuer',
    'mwst',
    'ust',
];

/**
 * MEHRDEUTIGE Woerter. Sie beginnen genauso oft eine echte Leistung
 * ("Gesamt 3 Begehungen je Monat", "Rabatt für Folgeaufträge"). Eine
 * Ordnungszahl entscheidet hier zugunsten der Leistung.
 */
const SUM_PATTERNS_WEAK = [
    'gesamt',
    'gesamtbetrag',
    'nettobetrag',
    'bruttobetrag',
    'netto',
    'brutto',
    'zzgl',
    'zuzueglich',
    'zuzüglich',
    'abzgl',
    'abzueglich',
    'abzüglich',
    'nachlass',
    'skonto',
    'rabatt',
];

/**
 * Summenzeilen, die nicht am Zeilenanfang beginnen oder eine Zahl voranstellen
 * ("19 % MwSt.", "19% Umsatzsteuer", "Summe Titel 03"). Der Praefix-Test allein
 * laesst sie durch.
 */
const SUM_REGEXES: RegExp[] = [
    /^\d{1,2}([.,]\d+)?\s*%\s*(mwst|ust|umsatz|mehrwert)/,
    /\b(summe|betrag)\s+(titel|los|abschnitt|hauptgruppe|leistungsbereich|position)\b/,
    /^(titel|los|abschnitt)[\s.:\-–]*\d*[\s.:\-–]*(summe|gesamt)\b/,
    /^(gesamt|summe)\s+(titel|los|abschnitt|hauptgruppe|leistungsbereich)\b/,
];

/** Titelzeilen tragen haeufig eine sprechende Marke statt Einheit/Menge. */
const HEADING_PATTERNS = ['titel', 'los', 'abschnitt', 'hauptgruppe', 'ordnungszahl', 'leistungsbereich'];

/**
 * Ab dieser Laenge ist eine Zeile ohne Menge/Einheit kein Titel mehr, sondern
 * Leistungstext — typischerweise der ueber einen Seitenumbruch fortgesetzte
 * Rest der vorigen Position. Als Ueberschrift wuerde sie den Abschnitt aller
 * folgenden Zeilen neu setzen; das ist der teuerste Einzelfehler des Imports.
 */
const HEADING_MAX_LEN = 90;

/**
 * Bedarfs-/Eventualposition. `\bnep\b` bewusst mit Wortgrenze — sonst trifft es
 * in "Nepomuk-Halle" oder "Schneppe".
 */
const NEP_REGEXES: RegExp[] = [
    /\bbedarfsposition(en)?\b/,
    /\bbedarfspos\b/,
    /\beventualposition(en)?\b/,
    /\beventualpos\b/,
    /\bnep\b/,
    /\bwahlposition(en)?\b/,
    /\balternativposition(en)?\b/,
    /\bnicht\s+eingerechnet\b/,
    /\bauf\s+abruf\b/,
];

const lower = (s: string): string => s.trim().toLowerCase();

/**
 * Treffer nur, wenn das Muster den Zeilenanfang bildet UND dort ein Wort endet.
 *
 * "Wortende" heisst Zeilenende, Leerzeichen oder Satzzeichen — ausdruecklich
 * NICHT Bindestrich und nicht Ziffer. Frueher galt jedes Zeichen ausser einem
 * Kleinbuchstaben als Wortende, und damit trafen die kurzen Summenwoerter
 * mitten in echte Leistungstexte: "Gesamt-SiGe-Plan erstellen",
 * "Netto-Nutzfläche", "Summen- und Übersichtsblatt",
 * "Umsatzsteuer-Identifikationsnummer" — alle wurden zur Summenzeile und
 * fielen damit aus dem Angebot.
 */
const WORD_END = /^[\s:.,;)\]!?%]?$/;
const HYPHENS = ['-', '–', '—'];
const startsWithAny = (text: string, patterns: string[], allowDigit = false): boolean =>
    patterns.some((p) => {
        if (!text.startsWith(p)) return false;
        const next = text.charAt(p.length);
        // Bindestrich direkt am Wort: zusammengesetztes Substantiv, keine Summe.
        if (HYPHENS.includes(next)) return false;
        // Bei den eindeutigen Summenwoertern darf direkt eine Ziffer folgen:
        // aus zusammengeklebtem Spaltentext wird "Übertrag12.345,67", und das
        // ist ganz sicher keine Leistungsposition.
        if (allowDigit && /\d/.test(next)) return true;
        return WORD_END.test(next);
    });

/** Eindeutige Summenzeile — schlaegt auch eine vorhandene Ordnungszahl. */
export const isStrongSumLine = (bezeichnung: string): boolean => {
    const text = lower(bezeichnung);
    if (!text) return false;
    if (startsWithAny(text, SUM_PATTERNS_STRONG, true)) return true;
    return SUM_REGEXES.some((re) => re.test(text));
};

/** Summenzeile? Praefix-Woerter ODER eines der Sondermuster. */
export const isTenderSumLine = (bezeichnung: string): boolean => {
    const text = lower(bezeichnung);
    if (!text) return false;
    return isStrongSumLine(text) || startsWithAny(text, SUM_PATTERNS_WEAK);
};

export const detectTenderNep = (bezeichnung: string): boolean => {
    const text = lower(bezeichnung);
    return !!text && NEP_REGEXES.some((re) => re.test(text));
};

/**
 * Ordnungszahl (OZ) einer LV-Position: "10", "01.02.0030", "1.1.10a", "2-3-40".
 * Ein Datum ("01.02.2026") ist keine OZ — die Jahresgruppe faengt der
 * Vierstellen-Test nicht ab, deshalb die eigene Ausnahme.
 */
const OZ_RE = /^\d{1,5}(?:[.\-/]\d{1,5})*[a-z]?$/i;
const DATE_LIKE_RE = /^\d{1,2}[.\-/]\d{1,2}[.\-/](19|20)\d{2}$/;

export const normalizeLvPosition = (position: string): string =>
    (position ?? '').trim().replace(/\s+/g, '').replace(/[.\-/]+$/, '');

export const isTenderPositionNumber = (position: string): boolean => {
    const p = normalizeLvPosition(position);
    if (!p) return false;
    if (DATE_LIKE_RE.test(p)) return false;
    return OZ_RE.test(p);
};

/**
 * Menge aus der KI-Antwort. Strings laufen ueber denselben Schreibweisen-
 * Parser wie Geldbetraege: ein eigener "Punkt raus, Komma zu Punkt" machte aus
 * "1.5" die Menge 15 — die zehnfache Leistung.
 *
 * Zusaetzlich wird die fuehrende Zahl aus Schreibweisen wie "12,5 m²",
 * "3 Stk" oder "ca. 40" herausgeloest. Vorher ergaben die alle 0 — und eine
 * Menge 0 ohne Einheit galt als Ueberschrift, womit die Position verschwand
 * und den Abschnitt aller Folgezeilen umbog.
 */
/**
 * Zieht Leerzeichen zusammen, die eine Tausendergruppe abtrennen ("1 000").
 *
 * Wiederholt und nicht global: ein globales Ersetzen verbraucht die Ziffer vor
 * dem ersten Leerzeichen, und die zweite Gruppe bleibt stehen — aus
 * "1 000 000" wurde dadurch 1000 statt 1000000. Still und um den Faktor 1000
 * daneben.
 */
const joinThousandSpaces = (s: string): string => {
    let out = s;
    for (let i = 0; i < 6; i++) {
        const next = out.replace(/(\d)[  ](\d{3})(?!\d)/, '$1$2');
        if (next === out) break;
        out = next;
    }
    return out;
};

export const parseTenderQuantity = (v: unknown): number => {
    if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
    if (typeof v !== 'string') return 0;

    // parseDecimalInput entfernt ALLE Leerzeichen. Bei "1 2" (zwei Zahlen in
    // einer Zelle) machte das die Menge 12 — eine erfundene Zahl, die niemand
    // als falsch erkennt. Erst pruefen, ob nach dem Zusammenziehen der
    // Tausendergruppen noch ein Leerzeichen zwischen Ziffern steht.
    const spaceJoined = joinThousandSpaces(v);
    if (!/\d\s+\d/.test(spaceJoined)) {
        const direct = parseDecimalInput(v);
        if (direct !== null && Number.isFinite(direct)) return direct;
    }

    // Apostroph und schmales Leerzeichen sind Tausendergliederung
    // (CH-Schreibweise, PDF-Exporte), ebenso ein Leerzeichen VOR einer
    // Dreiergruppe ("1 000"). Ein sonstiges Leerzeichen trennt dagegen zwei
    // Zahlen und darf sie nicht verkleben — "1. 000" ist die 1, nicht 1000.
    const cleaned = joinThousandSpaces(v.replace(/[’'  ]/g, ''))
        .replace(/^\s*(ca\.?|rd\.?|etwa|ungef(ä|ae)hr|~|>=|<=|>|<)\s*/i, '')
        .trim();

    const tokens = cleaned.match(/-?\d[\d.,]*/g);
    if (!tokens || !tokens.length) return 0;

    // Mehrere Zahlen in einer Zelle sind mehrdeutig. Nur wenn die Zelle MIT
    // der Zahl beginnt, ist die erste die Menge ("12,5 m²", "2,5-3,0"). Steht
    // Text davor ("Pos. 1.10 Menge 5"), waere jede Wahl geraten — und eine
    // falsche Menge ist schlimmer als eine erkennbare 0.
    if (tokens.length > 1) {
        if (!cleaned.startsWith(tokens[0])) return 0;
        // "1/2" ist ein Bruch, keine Menge 1.
        if (cleaned.charAt(tokens[0].length) === '/') return 0;
    }

    const n = parseDecimalInput(tokens[0]);
    return n !== null && Number.isFinite(n) ? n : 0;
};

export interface TenderLvClassification {
    kind: TenderLvRowKind;
    note: string;
    /** Unvollstaendige, aber uebernehmbare Leistungszeile — im Review markieren. */
    needsReview: boolean;
    /** Soll die Zeile vorausgewaehlt werden? Nur fuer kind === 'service' relevant. */
    preselect: boolean;
}

/**
 * Was ist diese Zeile? Die Einstufung entscheidet nur ueber die VORAUSWAHL —
 * im Review kann der Nutzer jede Zeile wieder anhaken. Es wird also nichts
 * stillschweigend verworfen, nur nicht mehr blind uebernommen.
 *
 * Reihenfolge der Pruefungen ist bewusst so und nicht anders:
 *  1. ohne Text -> unbrauchbar
 *  2. Summenzeile, ABER Menge UND Einheit schlagen das (eine Position darf
 *     "Uebertrag der Unterlage ... | psch | 1" heissen)
 *  3. Menge ODER Einheit vorhanden -> Leistung, ohne weitere Diskussion
 *  4. ab hier weder Menge noch Einheit: kurzer Titelmarker -> Ueberschrift
 *  5. mit Ordnungszahl -> Leistung (Pauschal-/Bedarfsposition), markiert
 *  6. kurz und ohne Nummer -> Ueberschrift
 *  7. lang und ohne Nummer -> Fortsetzungstext, sichtbar aber nicht angehakt
 */
export const classifyTenderLvRow = (
    row: Pick<TenderLvRow, 'bezeichnung' | 'einheit' | 'menge'> & { position?: string },
): TenderLvClassification => {
    const text = lower(row.bezeichnung);
    if (!text) {
        return { kind: 'empty', note: 'Ohne Bezeichnung — keine Leistung.', needsReview: false, preselect: false };
    }

    const hasUnit = !!lower(row.einheit);
    const hasQuantity = Number.isFinite(row.menge) && row.menge > 0;
    const hasOz = isTenderPositionNumber(row.position ?? '');

    // Menge UND Einheit schlagen JEDE Summenerkennung — eine Zwischensumme
    // rechnet nichts in "psch" ab ("Uebertrag der Unterlage fuer spaetere
    // Arbeiten | psch | 1" ist eine Leistung).
    // Eine Ordnungszahl schlaegt nur die MEHRDEUTIGEN Woerter: "Rabatt für
    // Folgeaufträge 01.90" ist eine Leistung, "1.4 Zwischensumme Titel 1"
    // dagegen bleibt eine Summenzeile — GAEB-Formulare schreiben die
    // Titelnummer regelmaessig in die Pos-Spalte der Summenzeile.
    const sumWins = !(hasUnit && hasQuantity)
        && (isStrongSumLine(text) || (isTenderSumLine(text) && !hasOz));
    if (sumWins) {
        return { kind: 'sum', note: 'Summen-/Übertragszeile — gehört nicht ins Angebot.', needsReview: false, preselect: false };
    }

    if (hasUnit || hasQuantity) {
        return { kind: 'service', note: '', needsReview: false, preselect: true };
    }

    // Ab hier: weder Menge noch Einheit.
    if (startsWithAny(text, HEADING_PATTERNS) && text.length <= HEADING_MAX_LEN) {
        return { kind: 'heading', note: 'Überschrift ohne Menge/Einheit — wird als Abschnitt übernommen.', needsReview: false, preselect: false };
    }

    if (hasOz) {
        return {
            kind: 'service',
            note: 'Ohne Menge und Einheit — Pauschal- oder Bedarfsposition? Bitte Menge/Einheit prüfen.',
            needsReview: true,
            preselect: true,
        };
    }

    if (text.length <= HEADING_MAX_LEN) {
        return { kind: 'heading', note: 'Überschrift ohne Menge/Einheit — wird als Abschnitt übernommen.', needsReview: false, preselect: false };
    }

    return {
        kind: 'service',
        note: 'Langer Text ohne Nummer, Menge und Einheit — vermutlich die Fortsetzung der Position darüber. Nicht vorausgewählt.',
        needsReview: true,
        preselect: false,
    };
};

const collapse = (s: string): string => s.trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Wiedererkennungsschluessel einer Zeile fuer den Re-Import. LV-Nummer gewinnt;
 * ohne sie muss die Bezeichnung herhalten. Beide Seiten (KI-Zeile und
 * bestehende Angebotsposition) muessen denselben Schluessel bilden.
 *
 * Der Endpunkt faellt weg: "1.1.10." und "1.1.10" sind dieselbe Position. Beim
 * Bepreisen des Original-PDFs (lvPriceMatch.normalizeLvPos) war das schon so,
 * beim Re-Import nicht — dieselbe Position kam dadurch doppelt ins Angebot.
 */
const rowKey = (position: string, bezeichnung: string): string => {
    const p = normalizeLvPosition(position).toLowerCase();
    return p ? `p:${p}` : `t:${collapse(bezeichnung)}`;
};

export const tenderLvRowKey = (row: Pick<TenderLvRow, 'position' | 'bezeichnung'>): string =>
    rowKey(row.position ?? '', row.bezeichnung ?? '');

export const quoteItemLvKey = (item: { lvPosition?: string; title?: string }): string =>
    rowKey(item.lvPosition ?? '', item.title ?? '');

const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v)).trim();

/**
 * Abschnitts-Vererbung + Duplikat-Erkennung ueber eine bereits eingestufte
 * Zeilenliste. Getrennt von der Rohdaten-Aufbereitung, damit dieselbe Ableitung
 * nach einer Korrektur im Review erneut laufen kann.
 */
const applyDerivations = (
    rows: TenderLvRowDraft[],
    existingKeys: ReadonlySet<string>,
    keepSelection: boolean,
): TenderLvRowDraft[] => {
    // Zuletzt gesehener Abschnitt. Die KI liefert in Dokumentreihenfolge, also
    // gilt er fuer alle folgenden Zeilen ohne eigenen Abschnitt.
    let currentSection = '';
    // Zweite, NICHT von Handeingaben beeinflusste Abschnittsspur. Nur sie geht
    // in den Duplikat-Schluessel: sonst wechselt eine Zeile beim Tippen im
    // Abschnittsfeld ihren Duplikat-Status und verliert dabei still den Haken.
    let sourceSection = '';
    // Innerhalb EINES Imports doppelte Zeilen (die KI wiederholt bei
    // Seitenumbruechen gern die Kopfzeile) nur einmal anhaken. Der Schluessel
    // traegt dabei den ABSCHNITT mit: "Baustellenbegehung" steht in Los 1, 2
    // und 3 desselben LV — ohne den Abschnitt wurden Los 2 und 3 stumm
    // abgewaehlt, mit ihm bleibt die wiederholte Kopfzeile derselben Gruppe
    // trotzdem erkennbar.
    const seen = new Set<string>();

    return rows.map((r) => {
        // Eine Handeingabe im Abschnittsfeld gewinnt immer — auch die leere.
        const own = r.abschnittOverride !== undefined ? r.abschnittOverride : r.sourceAbschnitt;

        if (r.kind === 'heading') {
            // Die Ueberschrift selbst wird nicht zur Position, gibt aber den
            // Abschnitt der folgenden Zeilen vor. Ein eigenes abschnitt-Feld
            // der KI hat Vorrang vor dem Ueberschriftentext — und eine
            // Handeingabe vor beidem, auch die leere.
            currentSection = r.abschnittOverride !== undefined
                ? r.abschnittOverride
                : (r.sourceAbschnitt || r.bezeichnung || currentSection);
            sourceSection = r.sourceAbschnitt || r.bezeichnung || sourceSection;
        } else {
            if (own) currentSection = own;
            if (r.sourceAbschnitt) sourceSection = r.sourceAbschnitt;
        }

        const abschnitt = r.abschnittOverride !== undefined
            ? r.abschnittOverride
            : (r.kind === 'heading' ? currentSection : (r.sourceAbschnitt || currentSection));

        const key = tenderLvRowKey(r);
        const scopedKey = key.startsWith('p:') ? key : `${key}@${collapse(sourceSection)}`;
        const duplicate = r.kind === 'service' && (existingKeys.has(key) || seen.has(scopedKey));
        if (r.kind === 'service') seen.add(scopedKey);

        const cls = classifyTenderLvRow(r);
        // Hat der Nutzer selbst entschieden, bleibt seine Entscheidung stehen —
        // begrenzt nur durch das, was ueberhaupt uebernehmbar ist. Sonst gilt
        // immer die frisch gerechnete Vorauswahl; sie darf NICHT aus einem
        // Zwischenzustand des Tippens konserviert werden.
        // Die Handentscheidung wird NICHT auf 'service' eingeengt: das Review
        // verspricht wortwoertlich "Anhaken übernimmt die übrigen trotzdem".
        // Wer eine als Ueberschrift oder Duplikat eingestufte Zeile bewusst
        // anhakt, will sie im Angebot haben — sonst waere die Checkbox dort
        // eine tote Schaltflaeche. Nur die leere Zeile geht nie: aus ihr
        // entstuende eine Position ohne Bezeichnung, deren Titel im Angebot
        // gesperrt ist und die sich nur noch loeschen laesst.
        const selected = (keepSelection && r.selectionTouched)
            ? (r.selected && r.kind !== 'empty')
            : (r.kind === 'service' && cls.preselect && !duplicate);

        return {
            ...r,
            // Summen- und Ueberschriftenzeilen erben den Abschnitt ebenfalls,
            // damit sie beim Anhaken nicht in der Catch-all-Gruppe landen.
            abschnitt,
            note: duplicate ? 'Steht mit dieser LV-Nummer bereits im Angebot.' : r.note,
            duplicate,
            selected,
        };
    });
};

/**
 * Rohe KI-Zeilen -> Review-Zeilen.
 *
 * `existingKeys` sind die Schluessel der bereits im Angebot stehenden
 * Positionen (quoteItemLvKey). Treffer werden als Duplikat markiert und NICHT
 * vorausgewaehlt, damit ein zweiter Import derselben Datei das Angebot nicht
 * verdoppelt.
 */
export const normalizeTenderLvRows = (
    raw: unknown[],
    existingKeys: ReadonlySet<string> = new Set(),
): TenderLvRowDraft[] => {
    const staged: TenderLvRowDraft[] = (Array.isArray(raw) ? raw : []).map((item) => {
        const r = (item ?? {}) as Record<string, unknown>;
        const position = str(r.position);
        const bezeichnung = str(r.bezeichnung);
        const einheit = str(r.einheit);
        const menge = parseTenderQuantity(r.menge);
        const sourceAbschnitt = str(r.abschnitt);
        const cls = classifyTenderLvRow({ position, bezeichnung, einheit, menge });

        return {
            position,
            bezeichnung,
            einheit,
            menge,
            abschnitt: sourceAbschnitt,
            sourceAbschnitt,
            assignment: normalizeAssignment(r.assignment),
            // 'nep' liefert die Ausricht-Stufe des Markiermodus, 'eventualposition'
            // der Prompt des Beleg-Imports. Beide lesen — sonst zaehlt eine
            // erkannte Bedarfsposition voll in die Angebotssumme.
            nep: typeof r.nep === 'boolean' ? r.nep
                : typeof r.eventualposition === 'boolean' ? r.eventualposition
                    : detectTenderNep(bezeichnung),
            kind: cls.kind,
            note: cls.note,
            needsReview: cls.needsReview,
            selected: false,
            duplicate: false,
        };
    });

    return applyDerivations(staged, existingKeys, false);
};

/**
 * Nach einer Korrektur im Review erneut einstufen und die Abschnitts-Vererbung
 * neu rechnen.
 *
 * Ohne das blieb eine als Ueberschrift eingestufte Pauschalposition auch dann
 * eine Ueberschrift, wenn der Nutzer "psch" nachtrug — und vor allem blieb die
 * bereits falsch vererbte Abschnittszuordnung ALLER Folgezeilen stehen. Jede
 * musste einzeln von Hand repariert werden, was in der Praxis niemand tut.
 *
 * Die vom Nutzer gesetzten Haken bleiben erhalten (`keepSelection`), soweit die
 * Zeile weiter uebernehmbar ist.
 */
export const reapplyTenderLvRows = <T extends TenderLvRowDraft>(
    rows: T[],
    existingKeys: ReadonlySet<string> = new Set(),
): T[] => {
    const reclassified = rows.map((r) => {
        const cls = classifyTenderLvRow(r);
        return { ...r, kind: cls.kind, note: cls.note, needsReview: cls.needsReview };
    });
    return applyDerivations(reclassified, existingKeys, true) as T[];
};

/** Kurzfassung fuer das Review: was hat der Import gefunden und was nicht. */
export interface TenderLvSummary {
    total: number;
    services: number;
    headings: number;
    sums: number;
    duplicates: number;
    selected: number;
    needsReview: number;
}

export const summarizeTenderLvRows = (rows: TenderLvRowDraft[]): TenderLvSummary => ({
    total: rows.length,
    services: rows.filter((r) => r.kind === 'service').length,
    headings: rows.filter((r) => r.kind === 'heading').length,
    sums: rows.filter((r) => r.kind === 'sum' || r.kind === 'empty').length,
    duplicates: rows.filter((r) => r.duplicate).length,
    selected: rows.filter((r) => r.selected).length,
    needsReview: rows.filter((r) => r.needsReview).length,
});

// ─── Plausibilitaet: was hat der Import womoeglich uebersehen? ────────────────

export interface TenderLvIssue {
    /** 'warn' = wahrscheinlich fehlt etwas, 'info' = nur Hinweis. */
    severity: 'warn' | 'info';
    text: string;
}

const parseOzSegments = (position: string): number[] | null => {
    const p = normalizeLvPosition(position);
    if (!p || !isTenderPositionNumber(p)) return null;
    const segs = p.replace(/[a-z]$/i, '').split(/[.\-/]/).filter(Boolean);
    if (!segs.length) return null;
    const nums = segs.map((s) => Number(s));
    return nums.every((n) => Number.isFinite(n)) ? nums : null;
};

/**
 * Luecken in der Ordnungszahl-Folge. Ein LV zaehlt innerhalb eines Titels in
 * festem Schritt hoch (10, 20, 30 oder 1, 2, 3). Fehlt dazwischen etwas, hat
 * die KI eine Position verschluckt — die einzige Moeglichkeit, das OHNE das
 * Originaldokument zu bemerken.
 *
 * Bewusst konservativ: nur bei mindestens drei Positionen je Gruppe, nur wenn
 * der Schritt sauber aufgeht. Ein Fehlalarm kostet Vertrauen, das der Import
 * gerade nicht hat.
 */
export const detectLvPositionGaps = (rows: Pick<TenderLvRowDraft, 'position' | 'kind'>[]): string[] => {
    const groups = new Map<string, number[]>();
    for (const r of rows) {
        if (r.kind !== 'service') continue;
        const segs = parseOzSegments(r.position);
        if (!segs || segs.length === 0) continue;
        const prefix = segs.slice(0, -1).join('.');
        const list = groups.get(prefix) ?? [];
        list.push(segs[segs.length - 1]);
        groups.set(prefix, list);
    }

    const out: string[] = [];
    for (const [prefix, values] of groups) {
        if (values.length < 3) continue;
        const diffs: number[] = [];
        for (let i = 1; i < values.length; i++) diffs.push(values[i] - values[i - 1]);
        const positives = diffs.filter((d) => d > 0);
        if (positives.length < 2) continue;

        // Der Schritt ist der HAEUFIGSTE Abstand, nicht der kleinste.
        // Nachtraeglich eingeschobene Positionen (10, 20, 30, 35) sind in LVs
        // der Normalfall; mit dem Minimum als Raster haette 5 gegolten und die
        // Pruefung haette zwei fehlende Nummern gemeldet, wo keine fehlen.
        const counts = new Map<number, number>();
        positives.forEach((d) => counts.set(d, (counts.get(d) ?? 0) + 1));
        const [step, stepHits] = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0];
        // Bei mehr als zwei Abstaenden muss sich der Schritt mindestens einmal
        // wiederholen — sonst ist er kein Raster, sondern Zufall. Bei genau
        // zwei Abstaenden gibt es nichts zu wiederholen; dort gilt der
        // kleinere als Schritt.
        if (step <= 0 || (stepHits < 2 && positives.length > 2)) continue;

        let missing = 0;
        for (const d of diffs) {
            if (d > step && d % step === 0) missing += d / step - 1;
        }
        if (missing > 0) {
            const label = prefix ? `Titel ${prefix}` : 'der Positionsfolge';
            out.push(missing === 1
                ? `${label}: 1 Ordnungszahl fehlt in der Folge.`
                : `${label}: ${missing} Ordnungszahlen fehlen in der Folge.`);
        }
    }
    return out;
};

/**
 * Alles, was gegen die eingelesenen Zeilen sprechen koennte, in einer Liste
 * fuer den Review-Kopf. Blockiert nie — der Nutzer entscheidet.
 */
export const checkTenderLvRows = (rows: TenderLvRowDraft[]): TenderLvIssue[] => {
    const out: TenderLvIssue[] = [];
    const services = rows.filter((r) => r.kind === 'service');

    for (const text of detectLvPositionGaps(rows)) {
        out.push({ severity: 'warn', text });
    }

    // Rueckwaertssprung = die Dokumentreihenfolge ist gebrochen. Daraus
    // entstehen im Angebot Positionen an der falschen Stelle.
    let lastKey: number[] | null = null;
    let backwards = 0;
    for (const r of services) {
        const segs = parseOzSegments(r.position);
        if (!segs) continue;
        if (lastKey) {
            const len = Math.max(lastKey.length, segs.length);
            for (let i = 0; i < len; i++) {
                const a = lastKey[i] ?? -1;
                const b = segs[i] ?? -1;
                if (b < a) { backwards++; break; }
                if (b > a) break;
            }
        }
        lastKey = segs;
    }
    if (backwards > 0) {
        out.push({
            severity: 'warn',
            text: `${backwards}× springt die Ordnungszahl zurück — die Reihenfolge stimmt nicht mit dem Dokument überein.`,
        });
    }

    const ozCount = new Map<string, number>();
    for (const r of services) {
        const p = normalizeLvPosition(r.position).toLowerCase();
        if (!p) continue;
        ozCount.set(p, (ozCount.get(p) ?? 0) + 1);
    }
    const dupOz = [...ozCount.values()].filter((n) => n > 1).length;
    if (dupOz > 0) {
        out.push({ severity: 'warn', text: `${dupOz} Ordnungszahl${dupOz === 1 ? '' : 'en'} kommen mehrfach vor.` });
    }

    const withoutOz = services.filter((r) => !isTenderPositionNumber(r.position)).length;
    if (withoutOz > 0 && services.length > 0) {
        out.push({
            severity: 'info',
            text: `${withoutOz} von ${services.length} Positionen ohne LV-Nummer — beim erneuten Einlesen nicht wiedererkennbar.`,
        });
    }

    const needsReview = rows.filter((r) => r.needsReview).length;
    if (needsReview > 0) {
        out.push({ severity: 'warn', text: `${needsReview} Zeile${needsReview === 1 ? '' : 'n'} ohne Menge und Einheit — bitte prüfen.` });
    }

    const nep = rows.filter((r) => r.kind === 'service' && r.nep).length;
    if (nep > 0) {
        out.push({ severity: 'info', text: `${nep} Bedarfs-/Eventualposition${nep === 1 ? '' : 'en'} erkannt — zählen nicht in die Angebotssumme.` });
    }

    return out;
};
